// @flow

import React, { PureComponent } from 'react';
import styled from 'styled-components';
import { colors, fonts } from 'shared/styleguide';
import { i18n, getCompanyCurrency } from 'shared/utils';
import { fetchQuery, graphql } from 'react-relay';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Button from 'shared/components/common/Button';
import AddressAutocomplete from 'main-app/components/AddressAutocomplete';

type LineItem = {
  description: string,
  sku: ?string,
  quantity: number,
  unitPrice: number,
  uom: ?string,
  itemId: ?string,
  itemName: ?string,
};

type Props = {
  extractedData: Object,
  fileName: string,
  fileUrl: string,
  onConfirm: (matchedData: Object) => void,
  onCancel: () => void,
};

type State = {
  customerId: ?string,
  customerName: ?string,
  customerOptions: Array<Object>,
  customerOrderNumber: string,
  shippingLine1: string,
  shippingLine2: string,
  shippingCity: string,
  shippingState: string,
  shippingZip: string,
  notes: string,
  currency: string,
  requiredAt: string,
  lineItems: Array<LineItem>,
  itemOptions: Array<Object>,
  submitting: boolean,
  customerSearch: string,
  customerDropdownOpen: boolean,
  showAddCustomerForm: boolean,
  newCustomerName: string,
  newCustomerEmail: string,
  newCustomerPhone: string,
  addingCustomer: boolean,
};

/* ==================== GQL ==================== */

const CUSTOMERS_QUERY = graphql`
  query POReviewFormCustomersQuery($search: String) {
    customers(first: 50, search: $search) {
      edges {
        node {
          id
          name
          shippingAddressLine1
          shippingAddressLine2
          shippingAddressCity
          shippingAddressState
          shippingAddressZip
        }
      }
    }
  }
`;

const ITEMS_QUERY = graphql`
  query POReviewFormItemsQuery($search: String) {
    items(first: 300, search: $search) {
      edges {
        node {
          id
          name
          quantityUOM {
            id
            name
            symbol
          }
        }
      }
    }
  }
`;

/* ==================== Styled ==================== */

const Container = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.lightBlueGrey};
  border-radius: 12px;
  overflow: hidden;
`;

const ReviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: ${colors.paleGrey};
  border-bottom: 1px solid ${colors.lightBlueGrey};
`;

const ReviewTitle = styled.div`
  ${fonts.headerBold};
  color: ${colors.charcoalGrey};
  font-size: 16px;
`;

const FileLink = styled.a`
  ${fonts.bodyRegular};
  color: ${colors.azul};
  font-size: 13px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Body = styled.div`
  padding: 20px;
`;

const SectionLabel = styled.div`
  ${fonts.smallBold};
  color: ${colors.coolGrey};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 11px;
  margin-bottom: 12px;
  margin-top: ${(p) => (p.first ? '0' : '24px')};
`;

const FieldRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Field = styled.div`
  flex: ${(p) => p.flex || 1};
`;

const Label = styled.label`
  display: block;
  ${fonts.smallBold};
  color: ${colors.charcoalGrey};
  margin-bottom: 4px;
  font-size: 12px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${colors.lightBlueGrey};
  border-radius: 6px;
  font-size: 13px;
  font-family: 'Source Sans Pro', -apple-system, sans-serif;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: ${colors.azul};
  }

  &::placeholder {
    color: ${colors.coolGrey};
  }
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${colors.lightBlueGrey};
  border-radius: 6px;
  font-size: 13px;
  font-family: 'Source Sans Pro', -apple-system, sans-serif;
  outline: none;
  box-sizing: border-box;
  background: ${colors.white};

  &:focus {
    border-color: ${colors.azul};
  }
`;

const LineItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
`;

const Th = styled.th`
  ${fonts.smallBold};
  color: ${colors.coolGrey};
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: left;
  padding: 8px 6px;
  border-bottom: 2px solid ${colors.lightBlueGrey};
`;

const Td = styled.td`
  padding: 8px 6px;
  border-bottom: 1px solid ${colors.lightBlueGrey};
  vertical-align: top;
`;

const SmallInput = styled(Input)`
  padding: 6px 8px;
  font-size: 12px;
`;

const SmallSelect = styled(SelectInput)`
  padding: 6px 8px;
  font-size: 12px;
`;

const MatchBadge = styled.span`
  ${fonts.smallBold};
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  ${(p) =>
    p.matched
      ? `background: #D4EDDA; color: #155724;`
      : `background: #FFF3CD; color: #856404;`}
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  color: ${colors.coolGrey};
  cursor: pointer;
  font-size: 16px;
  padding: 2px 6px;

  &:hover {
    color: ${colors.cherry};
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid ${colors.lightBlueGrey};
  background: ${colors.paleGrey};
`;

const LineTotal = styled.div`
  ${fonts.bodyRegular};
  font-size: 12px;
  color: ${colors.charcoalGrey};
  text-align: right;
  min-width: 70px;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 6px;
  ${fonts.headerBold};
  font-size: 15px;
  color: ${colors.charcoalGrey};
`;

const CustomerSearchWrapper = styled.div`
  position: relative;
`;

const CustomerDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 100;
  background: ${colors.white};
  border: 1px solid ${colors.lightBlueGrey};
  border-top: none;
  border-radius: 0 0 6px 6px;
  max-height: 220px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const CustomerDropdownItem = styled.div`
  padding: 8px 12px;
  font-size: 13px;
  font-family: 'Source Sans Pro', -apple-system, sans-serif;
  color: ${colors.charcoalGrey};
  cursor: pointer;

  &:hover {
    background: ${colors.paleGrey};
  }
`;

const CustomerDropdownAddBtn = styled(CustomerDropdownItem)`
  color: ${colors.azul};
  border-top: 1px solid ${colors.lightBlueGrey};
  font-weight: 600;
`;

const AddCustomerBox = styled.div`
  background: ${colors.paleGrey};
  border: 1px solid ${colors.lightBlueGrey};
  border-radius: 6px;
  padding: 14px;
  margin-top: 8px;
`;

const AddCustomerTitle = styled.div`
  ${fonts.smallBold};
  color: ${colors.charcoalGrey};
  font-size: 12px;
  margin-bottom: 10px;
`;

const AddCustomerRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`;

const AddCustomerActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 4px;
`;

/* ==================== Component ==================== */

class POReviewForm extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    const data = props.extractedData || {};
    const addr = data.shippingAddress || {};

    this.state = {
      customerId: null,
      customerName: data.customerName || '',
      customerOptions: [],
      customerOrderNumber: data.customerPoNumber || '',
      shippingLine1: addr.line1 || '',
      shippingLine2: addr.line2 || '',
      shippingCity: addr.city || '',
      shippingState: addr.state || '',
      shippingZip: addr.zip || '',
      notes: data.notes || '',
      currency: data.currency || getCompanyCurrency(),
      requiredAt: data.requiredDate || '',
      lineItems: (data.lineItems || []).map((li) => ({
        description: li.description || '',
        sku: li.sku || null,
        quantity: li.quantity || 0,
        unitPrice: li.unitPrice || 0,
        uom: li.uom || null,
        itemId: null,
        itemName: null,
      })),
      itemOptions: [],
      submitting: false,
      customerSearch: data.customerName || '',
      customerDropdownOpen: false,
      showAddCustomerForm: false,
      newCustomerName: data.customerName || '',
      newCustomerEmail: '',
      newCustomerPhone: '',
      addingCustomer: false,
    };
  }

  componentDidMount() {
    this.loadCustomers();
    this.loadItems();
  }

  loadCustomers = async () => {
    try {
      const res = await fetch('/graphql', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `query($search: String) {
            customers(first: 50, search: $search) {
              edges { node { id name shippingAddressLine1 shippingAddressLine2 shippingAddressCity shippingAddressState shippingAddressZip } }
            }
          }`,
          variables: { search: '' },
        }),
      });
      const json = await res.json();
      const options = ((json.data.customers || {}).edges || []).map((e) => e.node);
      this.setState({ customerOptions: options });

      // Auto-match by name
      if (this.state.customerName && !this.state.customerId) {
        const match = options.find(
          (c) => c.name.toLowerCase() === this.state.customerName.toLowerCase(),
        );
        if (match) {
          this.setState({
            customerId: match.id,
            customerName: match.name,
            customerSearch: match.name,
            shippingLine1: this.state.shippingLine1 || match.shippingAddressLine1 || '',
            shippingLine2: this.state.shippingLine2 || match.shippingAddressLine2 || '',
            shippingCity: this.state.shippingCity || match.shippingAddressCity || '',
            shippingState: this.state.shippingState || match.shippingAddressState || '',
            shippingZip: this.state.shippingZip || match.shippingAddressZip || '',
          });
        }
      }
    } catch (e) {
      // Ignore
    }
  };

  loadItems = async () => {
    try {
      const res = await fetch('/graphql', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `query($search: String) {
            items(first: 300, search: $search) {
              edges { node { id name quantityUOM { id name symbol } } }
            }
          }`,
          variables: { search: '' },
        }),
      });
      const json = await res.json();
      const options = ((json.data.items || {}).edges || []).map((e) => e.node);
      this.setState({ itemOptions: options }, () => {
        this.autoMatchItems(options);
      });
    } catch (e) {
      // Ignore
    }
  };

  autoMatchItems = (items: Array<Object>) => {
    this.setState((prev) => {
      const updated = prev.lineItems.map((li) => {
        if (li.itemId) return li;

        // Try matching by name or SKU
        const match = items.find((item) => {
          const nameMatch =
            item.name.toLowerCase() === (li.description || '').toLowerCase();
          const skuMatch =
            li.sku && item.name.toLowerCase().includes(li.sku.toLowerCase());
          return nameMatch || skuMatch;
        });

        if (match) {
          return { ...li, itemId: match.id, itemName: match.name };
        }
        return li;
      });
      return { lineItems: updated };
    });
  };

  handleCustomerSelect = (e: SyntheticEvent<HTMLSelectElement>) => {
    const id = e.currentTarget.value;
    const customer = this.state.customerOptions.find((c) => c.id === id);
    if (customer) {
      this.setState({
        customerId: id,
        customerName: customer.name,
        shippingLine1: customer.shippingAddressLine1 || this.state.shippingLine1,
        shippingLine2: customer.shippingAddressLine2 || this.state.shippingLine2,
        shippingCity: customer.shippingAddressCity || this.state.shippingCity,
        shippingState: customer.shippingAddressState || this.state.shippingState,
        shippingZip: customer.shippingAddressZip || this.state.shippingZip,
      });
    }
  };

  handleItemSelect = (index: number, itemId: string) => {
    const item = this.state.itemOptions.find((i) => i.id === itemId);
    this.setState((prev) => {
      const items = [...prev.lineItems];
      items[index] = {
        ...items[index],
        itemId,
        itemName: item ? item.name : null,
      };
      return { lineItems: items };
    });
  };

  handleLineItemChange = (index: number, field: string, value: any) => {
    this.setState((prev) => {
      const items = [...prev.lineItems];
      items[index] = { ...items[index], [field]: value };
      return { lineItems: items };
    });
  };

  handleRemoveLineItem = (index: number) => {
    this.setState((prev) => ({
      lineItems: prev.lineItems.filter((_, i) => i !== index),
    }));
  };

  handleAddLineItem = () => {
    this.setState((prev) => ({
      lineItems: [
        ...prev.lineItems,
        {
          description: '',
          sku: null,
          quantity: 0,
          unitPrice: 0,
          uom: null,
          itemId: null,
          itemName: null,
        },
      ],
    }));
  };

  handleCustomerSearchChange = (e: SyntheticEvent<HTMLInputElement>) => {
    this.setState({
      customerSearch: e.currentTarget.value,
      customerId: null,
      customerDropdownOpen: true,
    });
  };

  handleCustomerDropdownSelect = (customer: Object) => {
    this.setState({
      customerId: customer.id,
      customerName: customer.name,
      customerSearch: customer.name,
      customerDropdownOpen: false,
      showAddCustomerForm: false,
      shippingLine1: customer.shippingAddressLine1 || this.state.shippingLine1,
      shippingLine2: customer.shippingAddressLine2 || this.state.shippingLine2,
      shippingCity: customer.shippingAddressCity || this.state.shippingCity,
      shippingState: customer.shippingAddressState || this.state.shippingState,
      shippingZip: customer.shippingAddressZip || this.state.shippingZip,
    });
  };

  handleShowAddCustomer = () => {
    this.setState((prev) => ({
      showAddCustomerForm: true,
      customerDropdownOpen: false,
      newCustomerName: prev.customerSearch || prev.customerName || '',
    }));
  };

  handleCreateCustomer = async () => {
    const {
      newCustomerName,
      newCustomerEmail,
      newCustomerPhone,
      shippingLine1,
      shippingLine2,
      shippingCity,
      shippingState,
      shippingZip,
    } = this.state;

    if (!newCustomerName.trim()) {
      alert(i18n.t('Customer name is required.'));
      return;
    }

    this.setState({ addingCustomer: true });

    try {
      const input: Object = { name: newCustomerName.trim() };
      if (newCustomerEmail.trim()) input.email = newCustomerEmail.trim();
      if (newCustomerPhone.trim()) input.phone = newCustomerPhone.trim();
      if (shippingLine1) input.shippingAddressLine1 = shippingLine1;
      if (shippingLine2) input.shippingAddressLine2 = shippingLine2;
      if (shippingCity) input.shippingAddressCity = shippingCity;
      if (shippingState) input.shippingAddressState = shippingState;
      if (shippingZip) input.shippingAddressZip = shippingZip;

      const res = await fetch('/graphql', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `mutation AddCustomer($input: AddCustomerInput!) {
            addCustomer(input: $input) {
              customerEdge {
                node {
                  id name
                  shippingAddressLine1 shippingAddressLine2
                  shippingAddressCity shippingAddressState shippingAddressZip
                }
              }
            }
          }`,
          variables: { input },
        }),
      });

      const json = await res.json();
      if (json.errors && json.errors.length) {
        throw new Error(json.errors[0].message);
      }

      const customer = json.data.addCustomer.customerEdge.node;
      this.setState((prev) => ({
        customerOptions: [customer, ...prev.customerOptions],
        customerId: customer.id,
        customerName: customer.name,
        customerSearch: customer.name,
        showAddCustomerForm: false,
        addingCustomer: false,
        newCustomerName: '',
        newCustomerEmail: '',
        newCustomerPhone: '',
      }));
    } catch (e) {
      alert(e.message || i18n.t('Failed to create customer.'));
      this.setState({ addingCustomer: false });
    }
  };

  handleConfirm = () => {
    const {
      customerId,
      customerOrderNumber,
      shippingLine1,
      shippingLine2,
      shippingCity,
      shippingState,
      shippingZip,
      notes,
      currency,
      requiredAt,
      lineItems,
    } = this.state;

    if (!customerId) {
      alert(i18n.t('Please select a customer.'));
      return;
    }

    const unmatchedItems = lineItems.filter((li) => !li.itemId);
    if (unmatchedItems.length > 0) {
      alert(i18n.t('Please match all line items to your catalog.'));
      return;
    }

    if (lineItems.length === 0) {
      alert(i18n.t('At least one line item is required.'));
      return;
    }

    this.setState({ submitting: true });

    this.props.onConfirm({
      customerId,
      customerOrderNumber,
      shippingAddress: {
        line1: shippingLine1,
        line2: shippingLine2,
        line3: null,
        city: shippingCity,
        state: shippingState,
        zip: shippingZip,
      },
      notes,
      currency,
      requiredAt: requiredAt || null,
      discountPercent: 0,
      taxRate: 0,
      lineItems: lineItems.map((li) => ({
        itemId: li.itemId,
        orderedQuantity: Number(li.quantity) || 0,
        unitPrice: Number(li.unitPrice) || 0,
        discountPercent: 0,
      })),
    });
  };

  render() {
    const {
      customerId,
      customerOptions,
      customerOrderNumber,
      shippingLine1,
      shippingLine2,
      shippingCity,
      shippingState,
      shippingZip,
      notes,
      currency,
      requiredAt,
      lineItems,
      itemOptions,
      submitting,
      customerSearch,
      customerDropdownOpen,
      showAddCustomerForm,
      newCustomerName,
      newCustomerEmail,
      newCustomerPhone,
      addingCustomer,
    } = this.state;
    const { fileName, fileUrl } = this.props;

    const total = lineItems.reduce(
      (sum, li) => sum + (Number(li.quantity) || 0) * (Number(li.unitPrice) || 0),
      0,
    );

    return (
      <Container>
        <ReviewHeader>
          <ReviewTitle>{i18n.t('Review Extracted PO Data')}</ReviewTitle>
          <FileLink href={fileUrl} target="_blank" rel="noopener noreferrer">
            {fileName}
          </FileLink>
        </ReviewHeader>

        <Body>
          <SectionLabel first>{i18n.t('Customer Information')}</SectionLabel>
          <FieldRow>
            <Field flex={2}>
              <Label>{i18n.t('Customer')} *</Label>
              <CustomerSearchWrapper>
                <Input
                  value={customerSearch}
                  onChange={this.handleCustomerSearchChange}
                  onFocus={() => this.setState({ customerDropdownOpen: true })}
                  onBlur={() =>
                    setTimeout(() => this.setState({ customerDropdownOpen: false }), 150)
                  }
                  placeholder={i18n.t('Search customers...')}
                />
                {customerDropdownOpen && (
                  <CustomerDropdown>
                    {customerOptions
                      .filter(
                        (c) =>
                          !customerSearch ||
                          c.name.toLowerCase().includes(customerSearch.toLowerCase()),
                      )
                      .map((c) => (
                        <CustomerDropdownItem
                          key={c.id}
                          onMouseDown={() => this.handleCustomerDropdownSelect(c)}
                        >
                          {c.name}
                        </CustomerDropdownItem>
                      ))}
                    <CustomerDropdownAddBtn onMouseDown={this.handleShowAddCustomer}>
                      {i18n.t('+ Add New Customer')}
                    </CustomerDropdownAddBtn>
                  </CustomerDropdown>
                )}
              </CustomerSearchWrapper>
              {!customerId && !showAddCustomerForm && (
                <MatchBadge matched={false} style={{ marginTop: 4, display: 'inline-block' }}>
                  {i18n.t('Not matched')}
                </MatchBadge>
              )}
              {customerId && (
                <MatchBadge matched style={{ marginTop: 4, display: 'inline-block' }}>
                  {i18n.t('Matched')}
                </MatchBadge>
              )}
              {showAddCustomerForm && (
                <AddCustomerBox>
                  <AddCustomerTitle>{i18n.t('Add New Customer')}</AddCustomerTitle>
                  <AddCustomerRow>
                    <Field flex={2}>
                      <Label>{i18n.t('Name')} *</Label>
                      <Input
                        value={newCustomerName}
                        onChange={(e) => this.setState({ newCustomerName: e.target.value })}
                        placeholder={i18n.t('Company or contact name')}
                      />
                    </Field>
                  </AddCustomerRow>
                  <AddCustomerRow>
                    <Field>
                      <Label>{i18n.t('Email')}</Label>
                      <Input
                        value={newCustomerEmail}
                        onChange={(e) => this.setState({ newCustomerEmail: e.target.value })}
                        placeholder="email@example.com"
                      />
                    </Field>
                    <Field>
                      <Label>{i18n.t('Phone')}</Label>
                      <Input
                        value={newCustomerPhone}
                        onChange={(e) => this.setState({ newCustomerPhone: e.target.value })}
                        placeholder="555-000-0000"
                      />
                    </Field>
                  </AddCustomerRow>
                  <AddCustomerActions>
                    <Button
                      theme="transparent"
                      onClick={() => this.setState({ showAddCustomerForm: false })}
                      disabled={addingCustomer}
                    >
                      {i18n.t('Cancel')}
                    </Button>
                    <Button
                      theme="blue"
                      onClick={this.handleCreateCustomer}
                      loading={addingCustomer}
                    >
                      {i18n.t('Create Customer')}
                    </Button>
                  </AddCustomerActions>
                </AddCustomerBox>
              )}
            </Field>
            <Field>
              <Label>{i18n.t('Customer PO #')}</Label>
              <Input
                value={customerOrderNumber}
                onChange={(e) =>
                  this.setState({ customerOrderNumber: e.target.value })
                }
                placeholder={i18n.t('e.g. PO-12345')}
              />
            </Field>
          </FieldRow>

          <SectionLabel>{i18n.t('Shipping Address')}</SectionLabel>
          <FieldRow>
            <Field flex={2}>
              <Label>{i18n.t('Address Line 1')}</Label>
              <AddressAutocomplete
                value={shippingLine1}
                onChange={val => this.setState({ shippingLine1: val })}
                onAddressSelect={({ line1, city, state, zip }) => {
                  if (line1) this.setState({ shippingLine1: line1 });
                  if (city) this.setState({ shippingCity: city });
                  if (state) this.setState({ shippingState: state });
                  if (zip) this.setState({ shippingZip: zip });
                }}
                style={{ paddingBottom: 0 }}
              />
            </Field>
            <Field>
              <Label>{i18n.t('Address Line 2')}</Label>
              <Input
                value={shippingLine2}
                onChange={(e) => this.setState({ shippingLine2: e.target.value })}
              />
            </Field>
          </FieldRow>
          <FieldRow>
            <Field>
              <Label>{i18n.t('City')}</Label>
              <Input
                value={shippingCity}
                onChange={(e) => this.setState({ shippingCity: e.target.value })}
              />
            </Field>
            <Field>
              <Label>{i18n.t('State')}</Label>
              <Input
                value={shippingState}
                onChange={(e) => this.setState({ shippingState: e.target.value })}
              />
            </Field>
            <Field>
              <Label>{i18n.t('ZIP')}</Label>
              <Input
                value={shippingZip}
                onChange={(e) => this.setState({ shippingZip: e.target.value })}
              />
            </Field>
          </FieldRow>

          <SectionLabel>{i18n.t('Order Details')}</SectionLabel>
          <FieldRow>
            <Field>
              <Label>{i18n.t('Currency')}</Label>
              <SelectInput
                value={currency}
                onChange={(e) => this.setState({ currency: e.target.value })}
              >
                <option value="USD">USD</option>
                <option value="CAD">CAD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </SelectInput>
            </Field>
            <Field>
              <Label>{i18n.t('Required Date')}</Label>
              <Input
                type="date"
                value={requiredAt}
                onChange={(e) => this.setState({ requiredAt: e.target.value })}
              />
            </Field>
            <Field flex={2}>
              <Label>{i18n.t('Notes')}</Label>
              <Input
                value={notes}
                onChange={(e) => this.setState({ notes: e.target.value })}
                placeholder={i18n.t('Special instructions...')}
              />
            </Field>
          </FieldRow>

          <SectionLabel>{i18n.t('Line Items')}</SectionLabel>
          <LineItemsTable>
            <thead>
              <tr>
                <Th style={{ width: '30%' }}>{i18n.t('Extracted Description')}</Th>
                <Th style={{ width: '25%' }}>{i18n.t('Matched Item')} *</Th>
                <Th style={{ width: '10%' }}>{i18n.t('Qty')}</Th>
                <Th style={{ width: '12%' }}>{i18n.t('Unit Price')}</Th>
                <Th style={{ width: '12%' }}>{i18n.t('Line Total')}</Th>
                <Th style={{ width: '5%' }} />
              </tr>
            </thead>
            <tbody>
              {lineItems.map((li, idx) => {
                const lineTotal =
                  (Number(li.quantity) || 0) * (Number(li.unitPrice) || 0);
                return (
                  <tr key={idx}>
                    <Td>
                      <SmallInput
                        value={li.description}
                        onChange={(e) =>
                          this.handleLineItemChange(idx, 'description', e.target.value)
                        }
                      />
                      {li.sku && (
                        <div style={{ fontSize: 11, color: colors.coolGrey, marginTop: 2 }}>
                          SKU: {li.sku}
                        </div>
                      )}
                    </Td>
                    <Td>
                      <SmallSelect
                        value={li.itemId || ''}
                        onChange={(e) => this.handleItemSelect(idx, e.target.value)}
                      >
                        <option value="">{i18n.t('-- Select --')}</option>
                        {itemOptions.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </SmallSelect>
                      {li.itemId ? (
                        <MatchBadge matched style={{ marginTop: 2, display: 'inline-block', fontSize: 10 }}>
                          {i18n.t('Matched')}
                        </MatchBadge>
                      ) : (
                        <MatchBadge matched={false} style={{ marginTop: 2, display: 'inline-block', fontSize: 10 }}>
                          {i18n.t('Unmatched')}
                        </MatchBadge>
                      )}
                    </Td>
                    <Td>
                      <SmallInput
                        type="number"
                        value={li.quantity}
                        onChange={(e) =>
                          this.handleLineItemChange(idx, 'quantity', e.target.value)
                        }
                        style={{ width: 70 }}
                      />
                    </Td>
                    <Td>
                      <SmallInput
                        type="number"
                        step="0.01"
                        value={li.unitPrice}
                        onChange={(e) =>
                          this.handleLineItemChange(idx, 'unitPrice', e.target.value)
                        }
                        style={{ width: 90 }}
                      />
                    </Td>
                    <Td>
                      <LineTotal>
                        {currency} {lineTotal.toFixed(2)}
                      </LineTotal>
                    </Td>
                    <Td>
                      <RemoveBtn
                        onClick={() => this.handleRemoveLineItem(idx)}
                        title={i18n.t('Remove')}
                      >
                        &times;
                      </RemoveBtn>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </LineItemsTable>

          <div style={{ marginTop: 8 }}>
            <Button theme="transparent" onClick={this.handleAddLineItem}>
              {i18n.t('+ Add Line Item')}
            </Button>
          </div>

          <TotalRow>
            {i18n.t('Subtotal')}: {currency} {total.toFixed(2)}
          </TotalRow>
        </Body>

        <Footer>
          <Button theme="transparent" onClick={this.props.onCancel}>
            {i18n.t('Cancel')}
          </Button>
          <Button theme="blue" onClick={this.handleConfirm} loading={submitting}>
            {i18n.t('Create Sales Order')}
          </Button>
        </Footer>
      </Container>
    );
  }
}

export default POReviewForm;
